<script setup>
import { ref, reactive, computed, onMounted, onActivated, watch } from 'vue'
import api from '@/services/api'
import { useAuthStore } from '@/stores/auth'
import { usePaginacao } from '@/composables/usePaginacao'
import { useUiStore } from '@/stores/ui'
import PaginaAdministrar from '@/components/base/grid/PaginaAdministrar.vue'
import AdminTable from '@/components/base/grid/AdminTable.vue'
import CampoDataIntervalo from '@/components/base/campos/CampoDataIntervalo.vue'
import { formatarDataBR } from '@/utils/useDataBR'

const emit = defineEmits(['adicionar-item', 'editar-item', 'show-notification'])

const authStore = useAuthStore()
const uiStore = useUiStore()
const { paginacao } = usePaginacao(25)

const lista = ref([])
const loading = ref(false)
const erro = ref(null)
const filtrosAbertos = ref(false)

const sortField = ref('')
const sortOrder = ref('')

const listas = reactive({
  statusPrestacao: [
    { idListaSistemaItem: 'FIN_ST_PREST1', textoLista: 'Em Análise' },
    { idListaSistemaItem: 'FIN_ST_PREST2', textoLista: 'Aprovada' },
    { idListaSistemaItem: 'FIN_ST_PREST3', textoLista: 'Cancelada' }
  ]
})

const filtros = reactive({
  lstStatusPrestacao: '',
  dataInicio: '',
  dataFim: ''
})

const formatarValor = (valor) => {
  if (valor == null) return '-'
  return new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(valor)
}

const temPermissaoCompleta = computed(() => authStore.isAdminInterno || (authStore.permissoes && authStore.permissoes.includes('MODULO_COMPLETO')))
const temPermissaoVisualizacao = computed(() => true)
const podeVerAcoes = computed(() => temPermissaoCompleta.value || temPermissaoVisualizacao.value)

const colunas = computed(() => {
  const cols = [
    { field: 'idPrestacaoContas', label: 'ID', width: 80, sortable: true, slotName: 'id' },
    { field: 'nomeParceiro', label: 'Fornecedor', sortable: true, class: 'fw-medium', slotName: 'fornecedor' },
    { field: 'nomeUsuarioCriacao', label: 'Criado por', sortable: true, slotName: 'criador' },
    { field: 'valorTotalAbatidoPadrao', label: 'Valor Abatido', align: 'right', sortable: true, formatter: (v) => formatarValor(v) },
    { field: 'lstStatusPrestacao', label: 'Status', align: 'center', sortable: true, slotName: 'status' },
    { field: 'dataPrestacao', label: 'Data', align: 'center', sortable: true, slotName: 'data' }
  ]
  if (podeVerAcoes.value) {
    cols.push({ field: 'acoes', label: 'Ações', width: 160, align: 'right', slotName: 'acoes', resizable: false, sortable: false })
  }
  return cols
})

const sortResolver = (field, order, data) => {
  if (field !== 'lstStatusPrestacao') return null
  const resolvers = {
    lstStatusPrestacao: (row) => obterNomeStatusPrestacao(row)
  }
  const resolver = resolvers[field]
  if (!resolver) return null
  return [...data].sort((a, b) => {
    const cmp = resolver(a).localeCompare(resolver(b), 'pt-BR')
    return order === 'asc' ? cmp : -cmp
  })
}

const handleSortChange = () => { buscarDados(1) }

watch(() => authStore.empresaAtiva, (novaEmpresa) => {
  if (novaEmpresa) {
    buscarDados(1)
  } else {
    lista.value = []
    paginacao.value.TotalCount = 0
  }
})

const buscarDados = async (pagina = 1) => {
  if (!authStore.empresaAtiva && !authStore.isAdminInterno) return

  loading.value = true
  erro.value = null
  paginacao.value.CurrentPage = pagina

  try {
    const params = new URLSearchParams()
    params.append('pageNumber', pagina)
    params.append('pageSize', paginacao.value.PageSize)

    if (filtros.lstStatusPrestacao) params.append('lstStatusPrestacao', filtros.lstStatusPrestacao)
    if (filtros.dataInicio) params.append('dataInicio', filtros.dataInicio)
    if (filtros.dataFim) params.append('dataFim', filtros.dataFim)
    if (sortField.value) params.append('sortField', sortField.value)
    if (sortOrder.value) params.append('sortOrder', sortOrder.value)

    const response = await api.get(`/PrestacoesContas?${params.toString()}`)
    lista.value = response.data

    const paginationHeader = response.headers['x-pagination']
    if (paginationHeader) {
      const paginationData = JSON.parse(paginationHeader)
      paginacao.value.TotalCount = paginationData.TotalCount
      paginacao.value.TotalPages = paginationData.TotalPages
    } else {
      paginacao.value.TotalCount = lista.value.length
      paginacao.value.TotalPages = 1
    }
  } catch (error) {
    erro.value = error.response?.data?.message || 'Erro ao carregar prestações de contas.'
  } finally {
    loading.value = false
  }
}

const aplicarFiltros = () => { buscarDados(1) }

const limparFiltros = () => {
  filtros.lstStatusPrestacao = ''
  filtros.dataInicio = ''
  filtros.dataFim = ''
  buscarDados(1)
}

const mudarPagina = (page) => {
  if (page >= 1 && page <= (paginacao.value.TotalPages || 1)) buscarDados(page)
}

const mudarTamanhoPagina = (novoTamanho) => {
  paginacao.value.PageSize = novoTamanho
  buscarDados(1)
}

const editarRegistro = async (item) => {
  loading.value = true
  try {
    const response = await api.get(`/PrestacoesContas/${item.idPrestacaoContas}`)
    emit('editar-item', {
      ...response.data,
      isNovaPrestacao: false
    })
  } catch (error) {
    emit('show-notification', { message: 'Erro ao carregar dados da prestação.', type: 'error' })
  } finally {
    loading.value = false
  }
}

const aprovarPrestacao = async (item) => {
  if (!confirm(`Confirmar aprovação da prestação #${item.idPrestacaoContas}? Esta ação irá abater os títulos vinculados.`)) return
  loading.value = true
  try {
    await api.post(`/PrestacoesContas/${item.idPrestacaoContas}/Aprovar`)
    emit('show-notification', { message: 'Prestação de Contas aprovada com sucesso.', type: 'success' })
    buscarDados(paginacao.value.CurrentPage)
  } catch (error) {
    emit('show-notification', { message: error.response?.data?.message || 'Erro ao aprovar.', type: 'error' })
  } finally {
    loading.value = false
  }
}

const reabrirPrestacao = async (item) => {
  if (!confirm(`Reabrir a prestação #${item.idPrestacaoContas}? Os abatimentos serão revertidos.`)) return
  loading.value = true
  try {
    await api.post(`/PrestacoesContas/${item.idPrestacaoContas}/Reabrir`)
    emit('show-notification', { message: 'Prestação de Contas reaberta com sucesso.', type: 'success' })
    buscarDados(paginacao.value.CurrentPage)
  } catch (error) {
    emit('show-notification', { message: error.response?.data?.message || 'Erro ao reabrir.', type: 'error' })
  } finally {
    loading.value = false
  }
}

const obterCorStatusPrestacao = (item) => {
  if (item.lstStatusPrestacao === 'FIN_ST_PREST1') return 'badge-ghost-warning'
  if (item.lstStatusPrestacao === 'FIN_ST_PREST2') return 'badge-ghost-success'
  if (item.lstStatusPrestacao === 'FIN_ST_PREST3') return 'badge-ghost-danger'
  return 'badge-ghost-secondary'
}

const obterNomeStatusPrestacao = (item) => {
  return item.nomeStatusPrestacao || item.lstStatusPrestacao || '-'
}

onMounted(async () => {
  if (authStore.empresaAtiva || !authStore.isAdminInterno) buscarDados(1)
})

onActivated(() => { buscarDados(paginacao.value.CurrentPage || 1) })

defineExpose({ buscarDados })
</script>

<template>
  <PaginaAdministrar
    :titulo="uiStore.currentModuleTitle || 'Prestação de Contas'"
    subtitulo="Gestão de envelopes de prestação de contas"
    v-model:filtros-abertos="filtrosAbertos"
    :total-itens="paginacao.TotalCount"
    :itens-por-pagina="paginacao.PageSize"
    :pagina-atual="paginacao.CurrentPage"
    :mostrar-botao-adicionar="false"
    @mudar-pagina="mudarPagina"
    @mudar-tamanho-pagina="mudarTamanhoPagina"
    @atualizar="aplicarFiltros"
  >
    <template #acoes="{ toggleFiltros }">
      <button class="btn btn-dark" @click="toggleFiltros">
        <i class="bi bi-filter-lg me-2"></i> Filtros
      </button>
    </template>

    <template #filtros>
      <div class="row g-3 align-items-end">
        <div class="col-md-3">
          <label class="form-label">Status Prestação</label>
          <select class="form-select" v-model="filtros.lstStatusPrestacao" @change="aplicarFiltros">
            <option value="">Todos</option>
            <option v-for="s in listas.statusPrestacao" :key="s.idListaSistemaItem" :value="s.idListaSistemaItem">{{ s.textoLista }}</option>
          </select>
        </div>
        <div class="col-md-4">
          <CampoDataIntervalo id="filtroData" label="Período da Prestação" v-model:modelValueInicio="filtros.dataInicio" v-model:modelValueFim="filtros.dataFim" />
        </div>
        <div class="col-md-5 ms-auto text-end">
          <button class="btn btn-secondary me-2" @click="limparFiltros">Limpar</button>
          <button class="btn btn-primary" @click="aplicarFiltros">Aplicar Filtros</button>
        </div>
      </div>
    </template>

    <div v-if="loading" class="text-center p-5"><div class="spinner-border text-primary"></div></div>
    <div v-else-if="erro" class="alert alert-danger m-3 border-0 d-flex align-items-center">
      <i class="bi bi-exclamation-triangle-fill fs-4 me-3"></i> {{ erro }}
    </div>

    <AdminTable
      v-else
      :columns="colunas"
      :data="lista"
      :loading="loading"
      empty-message="Nenhuma prestação de contas encontrada."
      row-key="idPrestacaoContas"
      v-model:sort-field="sortField"
      v-model:sort-order="sortOrder"
      :sort-resolver="sortResolver"
      @sort-change="handleSortChange"
    >
      <template #id="{ row }">
        <span class="fw-bold text-body-secondary">#{{ row.idPrestacaoContas }}</span>
      </template>

      <template #fornecedor="{ row }">
        <span class="fw-medium">{{ row.nomeParceiro || '-' }}</span>
      </template>

      <template #criador="{ row }">
        <div class="d-flex align-items-center gap-2">
          <div v-if="row.avatarUsuarioCriacao" class="avatar-circle" :style="{ backgroundImage: `url(${row.avatarUsuarioCriacao})` }"></div>
          <div v-else class="avatar-circle avatar-placeholder">{{ (row.nomeUsuarioCriacao || '?')[0] }}</div>
          <span class="small">{{ row.nomeUsuarioCriacao || '-' }}</span>
        </div>
      </template>

      <template #status="{ row }">
        <span class="badge" :class="obterCorStatusPrestacao(row)">
          {{ obterNomeStatusPrestacao(row) }}
        </span>
      </template>

      <template #data="{ row }">
        <span>{{ formatarDataBR(row.dataPrestacao) }}</span>
      </template>

      <template #acoes="{ row }">
        <div class="d-flex justify-content-end gap-1 acoes-wrapper" v-if="podeVerAcoes">
          <button class="btn-icon" @click="editarRegistro(row)" :title="temPermissaoCompleta ? 'Ver / Editar Prestação' : 'Visualizar'">
            <i :class="temPermissaoCompleta ? 'bi bi-pencil-fill text-primary' : 'bi bi-eye-fill text-secondary'"></i>
          </button>
          <button v-if="row.lstStatusPrestacao === 'FIN_ST_PREST1' && temPermissaoCompleta" class="btn-icon text-success" @click="aprovarPrestacao(row)" title="Aprovar Prestação">
            <i class="bi bi-check-circle-fill"></i>
          </button>
          <button v-if="row.lstStatusPrestacao === 'FIN_ST_PREST2' && temPermissaoCompleta" class="btn-icon text-warning" @click="reabrirPrestacao(row)" title="Reabrir Prestação">
            <i class="bi bi-arrow-counterclockwise"></i>
          </button>
        </div>
      </template>
    </AdminTable>
  </PaginaAdministrar>
</template>

<style scoped>
.avatar-circle {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background-size: cover;
  background-position: center;
  flex-shrink: 0;
}
.avatar-placeholder {
  background: var(--bs-primary-bg-subtle);
  color: var(--bs-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.75rem;
}
</style>
